import { Link } from "react-router-dom";
import { ArrowUpRight, TriangleAlert } from "lucide-react";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { agentMap, priorityStripColor, taskStatusMeta } from "@/lib/mockData";
import { cn } from "@/lib/utils";

export default function TaskBlock({ task, phaseColor, left, width, top, height }) {
  const isHuman = task.owner === "HUMAN";
  const ownerAgent = agentMap[task.owner];
  const stripColor = priorityStripColor[task.priority] ?? priorityStripColor.med;
  const status = taskStatusMeta[task.status] ?? taskStatusMeta.todo;

  return (
    <Popover>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid={`task-block-${task.id}`}
              data-task-owner={isHuman ? "HUMAN" : "agent"}
              className={cn(
                "absolute overflow-hidden rounded-lg border border-black/[0.05] bg-white/95 text-left shadow-[0_1px_0_rgba(0,0,0,0.02)] transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_18px_-10px_rgba(15,15,25,0.25)]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400",
              )}
              style={{
                left: `${left}%`,
                width: `calc(${width}% - 4px)`,
                top,
                height,
              }}
            >
              <span
                aria-hidden
                className="absolute left-0 top-0 h-full w-[3px]"
                style={{ backgroundColor: stripColor }}
              />
              <div className="flex h-full items-center gap-2 pl-3 pr-2.5">
                <span
                  className="inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full"
                  style={{ backgroundColor: status.dot }}
                />
                <span className="truncate text-[11px] font-semibold text-zinc-900">
                  {task.title}
                </span>
                <span className="ml-auto flex flex-shrink-0 items-center">
                  {isHuman ? (
                    <span className="inline-flex items-center gap-0.5 rounded-full bg-orange-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em] text-orange-700">
                      <TriangleAlert className="h-2.5 w-2.5" strokeWidth={2.6} />
                    </span>
                  ) : (
                    <span
                      className="flex h-4 w-4 items-center justify-center rounded-full text-[8px] font-extrabold text-zinc-900"
                      style={{
                        backgroundColor: phaseColor?.band ?? "#FAFAFA",
                        boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)",
                      }}
                    >
                      {ownerAgent?.name?.[0]}
                    </span>
                  )}
                </span>
              </div>
              {/* progress fill */}
              <span
                aria-hidden
                className="absolute bottom-0 left-0 h-[2px]"
                style={{
                  width: `${task.progress ?? 0}%`,
                  backgroundColor: stripColor,
                  opacity: 0.55,
                }}
              />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-[240px] bg-zinc-900 text-white"
        >
          <div className="text-[11px] font-bold">{task.title}</div>
          <div className="mt-0.5 text-[10px] text-white/70">
            {isHuman ? "HUMAN" : `${ownerAgent?.name} · ${ownerAgent?.role}`}
          </div>
          <div className="mt-0.5 text-[10px] text-white/60">
            Day {task.startDay} → {task.startDay + task.durationDays - 1}
          </div>
          <div className="mt-0.5 text-[10px] text-white/60">
            {status.label} · {task.progress}%
          </div>
        </TooltipContent>
      </Tooltip>

      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-[280px] border-black/[0.06] bg-white p-4 text-zinc-900 shadow-xl"
      >
        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">
          {status.label} · {task.progress}%
        </div>
        <div className="mt-1 text-[14px] font-extrabold leading-tight tracking-tight">
          {task.title}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 text-[11px]">
          <div>
            <div className="text-zinc-400">Owner</div>
            <div className="mt-0.5 font-semibold text-zinc-800">
              {isHuman ? "HUMAN" : `${ownerAgent?.name} · ${ownerAgent?.role}`}
            </div>
          </div>
          <div>
            <div className="text-zinc-400">Window</div>
            <div className="mt-0.5 font-semibold text-zinc-800">
              Day {task.startDay} – {task.startDay + task.durationDays - 1}
            </div>
          </div>
        </div>
        <div className="mt-3 h-1 w-full overflow-hidden rounded-full bg-zinc-100">
          <div
            className="h-full rounded-full"
            style={{
              width: `${task.progress}%`,
              backgroundColor: stripColor,
            }}
          />
        </div>
        <Link
          to="/tasks"
          data-testid={`task-block-${task.id}-open-link`}
          className="mt-4 inline-flex items-center gap-1 text-[12px] font-semibold text-zinc-700 hover:text-black"
        >
          Open task
          <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.4} />
        </Link>
      </PopoverContent>
    </Popover>
  );
}
